import { cloneValue, deepFreeze, text, uniqueStrings } from './model-utils.js';
import { DOCUMENT_SCHEMA_VERSION } from './document-model.js';

/**
 * Captures an immutable, selected-only document for an exporter. Exporters must
 * never read live store state after receiving this object.
 * @param {Record<string, any>} stateOrDocument
 */
export function createExportSnapshot(stateOrDocument) {
  const document = stateOrDocument.document || stateOrDocument;
  const chapters = (document.chapters || []).filter(chapter => chapter.selected).map((chapter, index) => ({
    id: chapter.id,
    ordinal: index + 1,
    title: text(chapter.title, `章节 ${index + 1}`),
    note: text(chapter.note),
    selected: true,
    items: (chapter.items || []).filter(item => item.selected).map(item => ({ ...cloneValue(item), selected: true }))
  }));
  const itemCount = chapters.reduce((sum, chapter) => sum + chapter.items.length, 0);
  const imageItems = chapters.flatMap(chapter => chapter.items).filter(item => item.type === 'image');
  const snapshot = {
    schemaVersion: DOCUMENT_SCHEMA_VERSION,
    createdAt: new Date().toISOString(),
    project: cloneValue(document.project || {}),
    chapters,
    assetIds: uniqueStrings(imageItems.map(item => item.assetId)),
    stats: { chapterCount: chapters.length, itemCount, imageCount: imageItems.length }
  };
  return deepFreeze(snapshot);
}

/** @param {Record<string, any>} snapshot */
export function validateSnapshot(snapshot) {
  const errors = [];
  const warnings = [];
  if (snapshot?.schemaVersion !== DOCUMENT_SCHEMA_VERSION) errors.push('成果快照版本不受支持。');
  if (!text(snapshot?.project?.id)) errors.push('缺少项目 ID。');
  if (!text(snapshot?.project?.name)) errors.push('缺少项目名称。');
  if (!Array.isArray(snapshot?.chapters) || !snapshot.chapters.length) errors.push('没有可导出的章节。');

  const chapterIds = new Set();
  const itemIds = new Set();
  let imageCount = 0;
  for (const chapter of snapshot?.chapters || []) {
    if (!text(chapter.id)) errors.push('存在缺少 ID 的章节。');
    else if (chapterIds.has(chapter.id)) errors.push(`章节 ID 重复：${chapter.id}`);
    else chapterIds.add(chapter.id);
    if (!text(chapter.title)) errors.push(`章节 ${chapter.id || '?'} 缺少标题。`);
    if (!Array.isArray(chapter.items) || !chapter.items.length) warnings.push(`章节“${chapter.title || chapter.id}”没有选中的内容。`);
    for (const item of chapter.items || []) {
      if (!text(item.id)) errors.push(`章节 ${chapter.id || '?'} 存在缺少 ID 的内容。`);
      else if (itemIds.has(item.id)) errors.push(`内容 ID 重复：${item.id}`);
      else itemIds.add(item.id);
      if (!['text', 'list', 'facts', 'table', 'image'].includes(item.type)) errors.push(`不支持的内容类型：${item.type || '空'}`);
      if (item.type === 'text' && !text(item.text)) warnings.push(`文本“${item.title || item.id}”为空。`);
      if (item.type === 'list' && !Array.isArray(item.entries)) errors.push(`列表“${item.title || item.id}”格式无效。`);
      if (item.type === 'facts' && !Array.isArray(item.facts)) errors.push(`信息组“${item.title || item.id}”格式无效。`);
      if (item.type === 'table' && (!Array.isArray(item.columns) || !Array.isArray(item.rows))) errors.push(`表格“${item.title || item.id}”格式无效。`);
      if (item.type === 'image') {
        imageCount += 1;
        if (!text(item.assetId)) errors.push(`图片“${item.title || item.id}”缺少稳定资产 ID。`);
        if (!text(item.fileUrl)) errors.push(`图片“${item.title || item.id}”缺少文件端点。`);
      }
    }
  }
  if (!imageCount) warnings.push('当前成果没有选中的图片。');
  return { valid: errors.length === 0, errors, warnings };
}
